import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { FaInstagramSquare, FaLinkedin, FaYoutubeSquare } from "react-icons/fa";
import { FaSquareFacebook, FaXTwitter } from "react-icons/fa6";
import {
  LuGraduationCap, LuSearch, LuCheckCircle2, LuUsers,
  LuBuilding, LuBriefcase, LuShield, LuLanguages,
  LuPhone, LuMail, LuMapPin, LuArrowRight,
} from "react-icons/lu";
import { MdOutlineSchool } from "react-icons/md";

// ─── Design tokens ─────────────────────────────────────────────────────────────
const PRIMARY      = "#3073f1";
const PRIMARY_DARK = "#1a55c8";
const ACCENT       = "#45f5a1";

// ─── Translations ──────────────────────────────────────────────────────────────
const i18n = {
  en: {
    dir: "ltr",
    fontScale: "1",          // base
    navLinks:  ["Benefits", "Your Journey", "Destinations", "Application Status"],
    navAnchors:["#features", "#how-it-works", "#destinations", "#university-reply"],
    navCta: "Check My Status",
    heroBadge: "Your Study Abroad Journey Starts Here",
    heroTitle:  "Study Abroad with",
    heroSub:
      "Find your dream university, submit your application, and track every update — all in one place, stress-free.",
    heroBtn:    "Check My Application",
    heroBtn2:   "See How It Works",
    featuresLabel: "For Students",
    featuresTitle: "We make studying abroad simple for you",
    features: [
      { title: "Know Your Status Anytime",      desc: "No more waiting by the phone. Check your application status 24/7 using just your application code and passport number." },
      { title: "One Place for Everything",      desc: "All your university applications, documents, and offers are organised in one clear, easy-to-read place." },
      { title: "Real University Offers",        desc: "Receive conditional and unconditional offers directly through the platform — no paperwork confusion." },
      { title: "Fast & Secure",                 desc: "Your personal data and documents are protected. Only you can access your application using your passport number." },
      { title: "Multiple Universities at Once", desc: "Apply to several universities simultaneously and track each one separately without losing track." },
      { title: "Always Up to Date",             desc: "As soon as a university responds, your status updates instantly — you never miss an important decision." },
    ],
    statsLabel: "Students Trust StudyMore",
    statsTitle: "Numbers that speak for themselves",
    stats: [
      { label: "Students Enrolled" },
      { label: "Partner Universities" },
      { label: "Countries Covered" },
      { label: "Applications Handled" },
    ],
    howLabel: "Your Journey",
    howTitle: "How your application works",
    steps: [
      { title: "Talk to an Agent",          desc: "Your study abroad agent collects your details and helps you choose the right universities for your goals." },
      { title: "Application Submitted",     desc: "Your agent submits your application to the selected universities on your behalf — you don't have to do a thing." },
      { title: "University Reviews It",     desc: "The university carefully reviews your profile, qualifications, and documents to make a decision." },
      { title: "Check Your Status Here",    desc: "Come back to this page anytime, enter your code and passport, and instantly see if you've received an offer." },
    ],
    destLabel: "Study Destinations",
    destTitle: "Top Countries Our Students Study In",
    destSub: "We partner with top institutions across the world's most popular study destinations — find yours.",
    destinations: [
      { country: "United Kingdom", flag: "🇬🇧", unis: "120+ Universities", students: "3,200+" },
      { country: "Canada",         flag: "🇨🇦", unis: "85+ Universities",  students: "2,100+" },
      { country: "Australia",      flag: "🇦🇺", unis: "70+ Institutions",  students: "1,800+" },
      { country: "Germany",        flag: "🇩🇪", unis: "60+ Institutions",  students: "1,400+" },
      { country: "USA",            flag: "🇺🇸", unis: "90+ Universities",  students: "2,500+" },
      { country: "Malaysia",       flag: "🇲🇾", unis: "40+ Institutions",  students: "950+" },
    ],
    destStudents: "students enrolled",
    replyBadge: "Check Your Application",
    replyTitle: "Application Status",
    replySub: "Enter your Application Code (provided by your agent) and your Passport Number to instantly see your latest university status.",
    codeLabel:      "Application Code (University Code)",
    codePlaceholder:"e.g. UNI-2024-00123",
    passLabel:      "Passport Number",
    passPlaceholder:"e.g. A1234567",
    btnCheck:    "Check Application Status",
    btnChecking: "Checking…",
    errRequired: "Both fields are required.",
    errDefault:  "An error occurred. Please check your details and try again.",
    resStudent:  "Student",
    resUniversity:"University",
    resMajor:    "Major",
    resStatus:   "Status",
    resReply:    "University Reply",
    resCode:     "Application Code",
    resDate:     "Created At",
    resNoReply:  "No reply yet",
    msgFrom:     "📨 Message from University:",
    footerRights:"All rights reserved.",
    footerTagline:"Connecting students with their dream universities.",
    contactLabel:"Contact Us",
    contactItems:[
      { icon: "phone", value: "+962 6 000 0000" },
      { icon: "email", value: "info@studymore.com" },
      { icon: "map",   value: "Amman, Jordan" },
    ],
    statusMap: {
      pending:            "Pending",
      submitted:          "Submitted",
      accepted:           "Accepted",
      rejected:           "Rejected",
      conditional_offer:  "Conditional Offer",
      unconditional_offer:"Unconditional Offer",
      enrolled:           "Enrolled",
      withdrawn:          "Withdrawn",
    },
  },

  ar: {
    dir: "rtl",
    fontScale: "1.08",      // slightly larger for Arabic readability
    navLinks:  ["مميزاتك", "رحلتك", "وجهات الدراسة", "حالة الطلب"],
    navAnchors:["#features", "#how-it-works", "#destinations", "#university-reply"],
    navCta: "تحقق من حالتي",
    heroBadge: "رحلتك للدراسة في الخارج تبدأ من هنا",
    heroTitle:  "ادرس في الخارج مع",
    heroSub:
      "ابحث عن جامعة أحلامك، قدّم طلبك، وتابع كل تحديث — كل شيء في مكان واحد وبكل سهولة.",
    heroBtn:  "تحقق من طلبي",
    heroBtn2: "شوف كيف يعمل",
    featuresLabel: "للطلاب",
    featuresTitle: "نجعل الدراسة في الخارج سهلة عليك",
    features: [
      { title: "اعرف حالتك في أي وقت",       desc: "لا مزيد من الانتظار. تحقق من حالة طلبك على مدار الساعة باستخدام رمز طلبك ورقم جواز سفرك فقط." },
      { title: "كل شيء في مكان واحد",        desc: "جميع طلباتك الجامعية والمستندات والعروض منظّمة في مكان واحد واضح وسهل القراءة." },
      { title: "عروض قبول حقيقية",           desc: "استقبل عروض القبول المشروط وغير المشروط مباشرةً عبر المنصة — بدون تعقيد في الأوراق." },
      { title: "سريع وآمن تماماً",            desc: "بياناتك الشخصية ومستنداتك محمية. أنت الوحيد الذي يمكنه الوصول لطلبك برقم جوازك." },
      { title: "قدّم لعدة جامعات دفعة واحدة",desc: "تابع طلباتك لعدة جامعات في نفس الوقت وتابع كل واحدة على حدة بدون تشتت." },
      { title: "دائماً محدَّث",               desc: "فور ردّ الجامعة، تتحدث حالتك فوراً — لن تفوتك أي قرار مهم أبداً." },
    ],
    statsLabel: "طلاب يثقون بـ StudyMore",
    statsTitle: "أرقام تتحدث عن نفسها",
    stats: [
      { label: "طالب مسجَّل" },
      { label: "جامعة شريكة" },
      { label: "دولة نغطيها" },
      { label: "طلب تمّت معالجته" },
    ],
    howLabel: "رحلتك",
    howTitle: "كيف يسير طلبك خطوة بخطوة",
    steps: [
      { title: "تحدث مع وكيلك",              desc: "وكيل الدراسة في الخارج يجمع بياناتك ويساعدك في اختيار الجامعات المناسبة لأهدافك." },
      { title: "تقديم الطلب",               desc: "يُقدّم وكيلك طلبك للجامعات المختارة نيابةً عنك — ليس عليك القيام بأي شيء." },
      { title: "الجامعة تراجع ملفك",         desc: "تقوم الجامعة بمراجعة ملفك ومؤهلاتك ومستنداتك بعناية لاتخاذ قرارها." },
      { title: "تابع حالتك هنا",             desc: "عد لهذه الصفحة في أي وقت، أدخل رمز طلبك وجوازك، واعرف على الفور إن كنت حصلت على قبول." },
    ],
    destLabel: "وجهات الدراسة",
    destTitle: "أبرز الدول التي يدرس فيها طلابنا",
    destSub: "نتشارك مع أفضل المؤسسات في أكثر الوجهات الدراسية طلباً — اعثر على وجهتك.",
    destinations: [
      { country: "المملكة المتحدة", flag: "🇬🇧", unis: "+120 جامعة",  students: "+3,200" },
      { country: "كندا",            flag: "🇨🇦", unis: "+85 جامعة",   students: "+2,100" },
      { country: "أستراليا",        flag: "🇦🇺", unis: "+70 مؤسسة",  students: "+1,800" },
      { country: "ألمانيا",         flag: "🇩🇪", unis: "+60 مؤسسة",  students: "+1,400" },
      { country: "الولايات المتحدة",flag: "🇺🇸", unis: "+90 جامعة",   students: "+2,500" },
      { country: "ماليزيا",         flag: "🇲🇾", unis: "+40 مؤسسة",  students: "+950" },
    ],
    destStudents: "طالب مسجَّل",
    replyBadge: "تحقق من طلبك",
    replyTitle: "حالة الطلب",
    replySub: "أدخل رمز طلبك (المقدَّم من وكيلك) ورقم جواز سفرك لمعرفة آخر تحديث على طلبك في الجامعة.",
    codeLabel:      "رمز الطلب (رمز الجامعة)",
    codePlaceholder:"مثال: UNI-2024-00123",
    passLabel:      "رقم جواز السفر",
    passPlaceholder:"مثال: A1234567",
    btnCheck:    "تحقق من حالة الطلب",
    btnChecking: "جارٍ التحقق…",
    errRequired: "كلا الحقلين مطلوبان.",
    errDefault:  "حدث خطأ. يرجى التحقق من البيانات والمحاولة مجدداً.",
    resStudent:  "الطالب",
    resUniversity:"الجامعة",
    resMajor:    "التخصص",
    resStatus:   "الحالة",
    resReply:    "رد الجامعة",
    resCode:     "رمز الطلب",
    resDate:     "تاريخ الإنشاء",
    resNoReply:  "لا يوجد ردّ بعد",
    msgFrom:     "📨 رسالة من الجامعة:",
    footerRights: "جميع الحقوق محفوظة.",
    footerTagline:"نربط الطلاب بجامعات أحلامهم.",
    contactLabel:"تواصل معنا",
    contactItems:[
      { icon: "phone", value: "+962 6 000 0000" },
      { icon: "email", value: "info@studymore.com" },
      { icon: "map",   value: "عمّان، الأردن" },
    ],
    statusMap: {
      pending:            "قيد الانتظار",
      submitted:          "تم الإرسال",
      accepted:           "مقبول",
      rejected:           "مرفوض",
      conditional_offer:  "عرض مشروط",
      unconditional_offer:"عرض غير مشروط",
      enrolled:           "مسجَّل",
      withdrawn:          "تم السحب",
    },
  },
};

// ─── Status badge ──────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  pending:             { bg: "#fef3c7", color: "#92400e" },
  submitted:           { bg: "#dbeafe", color: "#1e40af" },
  accepted:            { bg: "#d1fae5", color: "#065f46" },
  rejected:            { bg: "#fee2e2", color: "#991b1b" },
  conditional_offer:   { bg: "#ede9fe", color: "#5b21b6" },
  unconditional_offer: { bg: "#d1fae5", color: "#065f46" },
  enrolled:            { bg: "#d1fae5", color: "#065f46" },
  withdrawn:           { bg: "#f3f4f6", color: "#374151" },
};

function StatusBadge({ status, t }) {
  const c = STATUS_COLORS[status] || { bg: "#f3f4f6", color: "#374151" };
  return (
    <span style={{ background: c.bg, color: c.color }}
      className="px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide">
      {t.statusMap[status] || status || "—"}
    </span>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-start justify-between py-3 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500 font-medium w-44 shrink-0">{label}</span>
      <span className="text-sm text-gray-800 font-semibold text-right break-words max-w-xs">{value || "—"}</span>
    </div>
  );
}

// ─── Animated counter ──────────────────────────────────────────────────────────
function AnimCounter({ target, suffix = "" }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        let v = 0;
        const step = Math.ceil(target / 60);
        const timer = setInterval(() => {
          v = Math.min(v + step, target);
          setCount(v);
          if (v >= target) clearInterval(timer);
        }, 20);
        observer.disconnect();
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);
  return (
    <span ref={ref} className="text-4xl md:text-5xl font-extrabold" style={{ color: PRIMARY }}>
      {count.toLocaleString()}{suffix}
    </span>
  );
}

const FEATURE_META = [
  { icon: <LuUsers size={24} />,        iconBg: PRIMARY,    gradient: `linear-gradient(135deg,${PRIMARY}18,${PRIMARY}05)` },
  { icon: <MdOutlineSchool size={24} />, iconBg: "#059669", gradient: `linear-gradient(135deg,${ACCENT}33,${ACCENT}08)` },
  { icon: <LuBuilding size={24} />,      iconBg: "#7c3aed", gradient: "linear-gradient(135deg,#7c3aed18,#7c3aed05)" },
  { icon: <LuShield size={24} />,        iconBg: "#d97706", gradient: "linear-gradient(135deg,#f59e0b18,#f59e0b05)" },
  { icon: <LuBriefcase size={24} />,     iconBg: "#dc2626", gradient: "linear-gradient(135deg,#ef444418,#ef444405)" },
  { icon: <LuCheckCircle2 size={24} />,  iconBg: PRIMARY,   gradient: `linear-gradient(135deg,${PRIMARY}18,#7c3aed10)` },
];

const SOCIAL_LINKS = [
  { Icon: FaLinkedin,        href: "https://www.linkedin.com/company/studymore" },
  { Icon: FaSquareFacebook,  href: "https://www.facebook.com/studymore" },
  { Icon: FaInstagramSquare, href: "https://www.instagram.com/studymore" },
  { Icon: FaYoutubeSquare,   href: "https://www.youtube.com/@studymore" },
  { Icon: FaXTwitter,        href: "https://www.twitter.com/studymore" },
];

// ─── Contact icon helper ───────────────────────────────────────────────────────
function ContactIcon({ type }) {
  const cls = "shrink-0";
  if (type === "phone") return <LuPhone size={16} className={cls} />;
  if (type === "email") return <LuMail size={16} className={cls} />;
  return <LuMapPin size={16} className={cls} />;
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function HomePage() {
  const { data: settingData } = useSelector((state) => state.setting);
  const [logoError, setLogoError] = useState(false);
  const [lang, setLang]           = useState("en");

  // University Reply state
  const [uniCode,       setUniCode]       = useState("");
  const [passport,      setPassport]      = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResult,  setLookupResult]  = useState(null);
  const [lookupError,   setLookupError]   = useState("");

  const t           = i18n[lang];
  const isRTL       = lang === "ar";
  const companyName = settingData?.companyName || "StudyMore";
  const logoSrc     = settingData?.logo;

  // ── Font scale for Arabic ──────────────────────────────────────────────────
  const fontStyle = isRTL
    ? { fontSize: "107%", lineHeight: "1.85", letterSpacing: "0" }
    : {};

  const handleLookup = async (e) => {
    e.preventDefault();
    setLookupError(""); setLookupResult(null);
    if (!uniCode.trim() || !passport.trim()) { setLookupError(t.errRequired); return; }
    setLookupLoading(true);
    try {
      const { data } = await axios.post("/public/application/check", {
        universityCode: uniCode.trim(),
        passportNumber: passport.trim(),
      });
      setLookupResult(data);
    } catch (err) {
      setLookupError(err?.response?.data?.error || t.errDefault);
    } finally {
      setLookupLoading(false);
    }
  };

  // ── Logo element ───────────────────────────────────────────────────────────
  const logoEl = logoSrc && !logoError ? (
    <img src={logoSrc} alt="logo" className="h-10 w-auto object-contain"
      onError={() => setLogoError(true)} />
  ) : companyName.includes(" ") ? (
    <span className="text-xl font-bold tracking-tight">
      <span className="text-white">{companyName.split(" ")[0]}</span>{" "}
      <span style={{ color: ACCENT }}>{companyName.slice(companyName.indexOf(" ") + 1)}</span>
    </span>
  ) : (
    <span className="text-xl font-bold tracking-tight" style={{ color: ACCENT }}>{companyName}</span>
  );

  return (
    <div dir={t.dir} style={fontStyle}
      className="min-h-screen font-[Poppins,sans-serif] bg-white overflow-x-hidden">

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* NAVBAR                                                              */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#060d1f]/95 backdrop-blur-md"
        style={{ borderBottom: `1px solid rgba(255,255,255,0.08)` }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-6">

          {/* Logo + tagline */}
          <div className="flex items-center gap-3 shrink-0">
            {logoEl}
            <div className="hidden lg:block h-6 w-px bg-white/20" />
            <span className="hidden lg:block text-xs text-gray-400 font-medium">
              {isRTL ? "الدراسة في الخارج" : "Study Abroad CRM"}
            </span>
          </div>

          {/* Nav links — desktop */}
          <div className="hidden md:flex items-center gap-1 flex-1 justify-center">
            {t.navLinks.map((item, i) => (
              <a key={item} href={t.navAnchors[i]}
                className="px-4 py-2 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/8 transition-all font-medium whitespace-nowrap"
                style={{ "--tw-bg-opacity": "1" }}>
                {item}
              </a>
            ))}
          </div>

          {/* Right section: CTA + Lang toggle */}
          <div className="flex items-center gap-3 shrink-0">
            <a href="#university-reply"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
              style={{ background: `linear-gradient(90deg,${PRIMARY},${PRIMARY_DARK})` }}>
              <LuSearch size={14} />
              <span>{t.navCta}</span>
            </a>
            <button
              onClick={() => setLang(lang === "en" ? "ar" : "en")}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border border-white/20 text-gray-200 hover:bg-white/10 transition-all whitespace-nowrap">
              <LuLanguages size={14} />
              {lang === "en" ? "عربي" : "EN"}
            </button>
          </div>
        </div>

        {/* Mobile nav links strip */}
        <div className="md:hidden flex items-center gap-1 overflow-x-auto px-4 pb-2 no-scrollbar">
          {t.navLinks.map((item, i) => (
            <a key={item} href={t.navAnchors[i]}
              className="px-3 py-1.5 rounded-lg text-xs text-gray-300 hover:text-white whitespace-nowrap border border-white/10 hover:border-white/25 transition-all">
              {item}
            </a>
          ))}
        </div>
      </nav>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* HERO                                                                */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16"
        style={{ background: "linear-gradient(135deg,#060d1f 0%,#0d1f4e 55%,#060d1f 100%)" }}>

        {/* Background blobs */}
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full opacity-20 blur-[130px] animate-pulse"
          style={{ background: PRIMARY }} />
        <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full opacity-15 blur-[130px] animate-pulse"
          style={{ background: ACCENT, animationDelay: "1.2s" }} />
        {/* Grid overlay */}
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: "linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)",
          backgroundSize: "65px 65px",
        }} />

        <div className="relative z-10 text-center px-4 sm:px-8 max-w-5xl mx-auto py-16">
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold mb-8 border"
            style={{ background: `${PRIMARY}22`, borderColor: `${PRIMARY}55`, color: ACCENT }}>
            <LuGraduationCap size={15} /> {t.heroBadge}
          </div>

          <h1 className="font-extrabold text-white leading-tight mb-6"
            style={{ fontSize: isRTL ? "clamp(2.5rem,7vw,5.5rem)" : "clamp(2.75rem,8vw,6rem)" }}>
            {t.heroTitle}{" "}
            <span className="bg-clip-text text-transparent"
              style={{ backgroundImage: `linear-gradient(90deg,${PRIMARY},${ACCENT})` }}>
              {companyName}
            </span>
          </h1>

          <p className="text-gray-300 max-w-2xl mx-auto mb-10 leading-relaxed"
            style={{ fontSize: isRTL ? "1.15rem" : "1.1rem" }}>
            {t.heroSub}
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <a href="#university-reply"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-white shadow-lg transition-all hover:scale-105 hover:shadow-2xl"
              style={{ background: PRIMARY, boxShadow: `0 0 40px ${PRIMARY}55`, fontSize: isRTL ? "1rem" : "0.95rem" }}>
              <LuSearch size={18} /> {t.heroBtn}
            </a>
            <a href="#features"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold border transition-all hover:bg-white/10"
              style={{ borderColor: "rgba(255,255,255,0.22)", color: "#fff", fontSize: isRTL ? "1rem" : "0.95rem" }}>
              {t.heroBtn2} <LuArrowRight size={16} />
            </a>
          </div>

          {/* Trust bar */}
          <div className="mt-16 flex flex-wrap justify-center gap-6 md:gap-10">
            {[
              { n: "12,000+", l: isRTL ? "طالب" : "Students" },
              { n: "250+",    l: isRTL ? "جامعة" : "Universities" },
              { n: "800+",    l: isRTL ? "وكيل" : "Agents" },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl font-extrabold" style={{ color: ACCENT }}>{s.n}</div>
                <div className="text-xs text-gray-400 mt-0.5">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-5 h-9 rounded-full border-2 border-white/25 flex items-start justify-center pt-1.5">
            <div className="w-1 h-2 rounded-full bg-white/60 animate-[slideDown_1.5s_ease_infinite]" />
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* FEATURES                                                            */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <section id="features" className="py-24 bg-[#f7f8fe]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: PRIMARY }}>
              {t.featuresLabel}
            </p>
            <h2 style={{ fontSize: isRTL ? "2.1rem" : "2rem" }}
              className="font-extrabold text-gray-900">{t.featuresTitle}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.features.map((f, i) => (
              <div key={i}
                className="rounded-2xl p-7 border border-gray-100 hover:border-blue-200 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                style={{ background: FEATURE_META[i].gradient }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 text-white"
                  style={{ background: FEATURE_META[i].iconBg }}>
                  {FEATURE_META[i].icon}
                </div>
                <h3 className="font-bold text-gray-900 mb-2"
                  style={{ fontSize: isRTL ? "1.08rem" : "1rem" }}>{f.title}</h3>
                <p className="text-gray-600 leading-relaxed"
                  style={{ fontSize: isRTL ? "0.95rem" : "0.875rem" }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* STATS                                                               */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <section className="py-24"
        style={{ background: "linear-gradient(135deg,#060d1f 0%,#0d1f4e 100%)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: ACCENT }}>
              {t.statsLabel}
            </p>
            <h2 className="font-extrabold text-white"
              style={{ fontSize: isRTL ? "2.1rem" : "2rem" }}>{t.statsTitle}</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
            {[12000, 250, 800, 45000].map((target, i) => (
              <div key={i} className="rounded-2xl p-8 border border-white/10"
                style={{ background: "rgba(255,255,255,0.05)", backdropFilter: "blur(8px)" }}>
                <AnimCounter target={target} suffix="+" />
                <div className="text-gray-300 mt-2 font-medium"
                  style={{ fontSize: isRTL ? "0.95rem" : "0.875rem" }}>{t.stats[i].label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* HOW IT WORKS                                                        */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <section id="how-it-works" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: PRIMARY }}>
              {t.howLabel}
            </p>
            <h2 className="font-extrabold text-gray-900"
              style={{ fontSize: isRTL ? "2.1rem" : "2rem" }}>{t.howTitle}</h2>
          </div>
          <div className="flex flex-col md:flex-row items-start justify-between gap-10 relative">
            <div className="hidden md:block absolute top-8 left-0 right-0 h-0.5 z-0"
              style={{ background: `linear-gradient(90deg,${PRIMARY},${ACCENT})` }} />
            {t.steps.map((w, i) => (
              <div key={i} className="relative z-10 flex-1 flex flex-col items-center text-center px-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-extrabold mb-5 shadow-xl"
                  style={{ background: `linear-gradient(135deg,${PRIMARY},#7c3aed)` }}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-bold text-gray-900 mb-2"
                  style={{ fontSize: isRTL ? "1.05rem" : "0.95rem" }}>{w.title}</h3>
                <p className="text-gray-500 leading-relaxed"
                  style={{ fontSize: isRTL ? "0.93rem" : "0.875rem" }}>{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* ★ NEW: STUDY DESTINATIONS                                          */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <section id="destinations" className="py-24 bg-[#f7f8fe]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: PRIMARY }}>
              {t.destLabel}
            </p>
            <h2 className="font-extrabold text-gray-900 mb-4"
              style={{ fontSize: isRTL ? "2.1rem" : "2rem" }}>{t.destTitle}</h2>
            <p className="text-gray-500 max-w-xl mx-auto"
              style={{ fontSize: isRTL ? "1rem" : "0.95rem" }}>{t.destSub}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.destinations.map((d, i) => (
              <div key={i}
                className="group relative bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-xl hover:border-blue-200 transition-all duration-300 hover:-translate-y-1 overflow-hidden">
                {/* Background accent */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: `linear-gradient(135deg,${PRIMARY}06,${ACCENT}08)` }} />

                <div className="relative z-10">
                  {/* Flag + country */}
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-4xl">{d.flag}</span>
                    <div>
                      <h3 className="font-bold text-gray-900"
                        style={{ fontSize: isRTL ? "1.08rem" : "1rem" }}>{d.country}</h3>
                      <p className="text-xs text-gray-400 mt-0.5">{d.unis}</p>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-gray-100 my-4" />

                  {/* Student count */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{t.destStudents}</span>
                    <span className="font-extrabold" style={{ color: PRIMARY, fontSize: "1.15rem" }}>
                      {d.students}
                    </span>
                  </div>

                  {/* Hover CTA */}
                  <div className="mt-4 flex items-center gap-1 text-xs font-semibold opacity-0 group-hover:opacity-100 transition-all duration-300"
                    style={{ color: PRIMARY }}>
                    <LuArrowRight size={12} />
                    <span>{isRTL ? "استكشف البرامج" : "Explore programmes"}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* UNIVERSITY REPLY                                                    */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <section id="university-reply" className="py-24"
        style={{ background: "linear-gradient(135deg,#060d1f 0%,#0d1f4e 100%)" }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold mb-5 border"
              style={{ background: `${ACCENT}18`, borderColor: `${ACCENT}44`, color: ACCENT }}>
              <MdOutlineSchool size={14} /> {t.replyBadge}
            </div>
            <h2 className="font-extrabold text-white mb-4"
              style={{ fontSize: isRTL ? "2.2rem" : "2rem" }}>{t.replyTitle}</h2>
            <p className="text-gray-300 max-w-xl mx-auto"
              style={{ fontSize: isRTL ? "1rem" : "0.95rem" }}>{t.replySub}</p>
          </div>

          {/* Search card */}
          <div className="bg-white rounded-3xl shadow-2xl border border-white/10 p-8 md:p-10">
            <form onSubmit={handleLookup} className="flex flex-col gap-5">
              <div>
                <label className="block font-semibold text-gray-700 mb-2"
                  style={{ fontSize: isRTL ? "0.97rem" : "0.875rem" }}>
                  {t.codeLabel}
                </label>
                <input type="text" id="university-code-input" value={uniCode}
                  onChange={(e) => setUniCode(e.target.value)}
                  placeholder={t.codePlaceholder}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none transition-all"
                  style={{ fontSize: isRTL ? "0.97rem" : "0.875rem" }}
                  onFocus={(e) => (e.target.style.borderColor = PRIMARY)}
                  onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")} />
              </div>
              <div>
                <label className="block font-semibold text-gray-700 mb-2"
                  style={{ fontSize: isRTL ? "0.97rem" : "0.875rem" }}>
                  {t.passLabel}
                </label>
                <input type="text" id="passport-number-input" value={passport}
                  onChange={(e) => setPassport(e.target.value)}
                  placeholder={t.passPlaceholder}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none transition-all"
                  style={{ fontSize: isRTL ? "0.97rem" : "0.875rem" }}
                  onFocus={(e) => (e.target.style.borderColor = PRIMARY)}
                  onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")} />
              </div>

              {lookupError && (
                <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700"
                  style={{ fontSize: isRTL ? "0.93rem" : "0.875rem" }}>
                  <span className="font-bold mt-0.5">!</span>
                  <span>{lookupError}</span>
                </div>
              )}

              <button type="submit" disabled={lookupLoading} id="check-application-btn"
                className="w-full flex items-center justify-center gap-2 py-4 rounded-xl font-semibold text-white transition-all hover:opacity-90 active:scale-95 disabled:opacity-60"
                style={{ background: `linear-gradient(90deg,${PRIMARY},${PRIMARY_DARK})`, fontSize: isRTL ? "1rem" : "0.9rem" }}>
                {lookupLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    {t.btnChecking}
                  </>
                ) : (
                  <><LuSearch size={16} /> {t.btnCheck}</>
                )}
              </button>
            </form>

            {/* Result */}
            {lookupResult && (
              <div className="mt-8 border-t pt-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-xl"
                    style={{ background: `linear-gradient(135deg,${PRIMARY},#7c3aed)` }}>
                    {(lookupResult.student?.firstName?.[0] || "S").toUpperCase()}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-lg">
                      {[lookupResult.student?.firstName, lookupResult.student?.middleName, lookupResult.student?.lastName].filter(Boolean).join(" ")}
                    </p>
                    <StatusBadge status={lookupResult.application?.status} t={t} />
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4 divide-y divide-gray-100">
                  <InfoRow label={t.resStudent}
                    value={[lookupResult.student?.firstName, lookupResult.student?.lastName].filter(Boolean).join(" ")} />
                  <InfoRow label={t.resUniversity} value={lookupResult.application?.university} />
                  <InfoRow label={t.resMajor}      value={lookupResult.application?.major} />
                  <div className="flex items-start justify-between py-3 border-b border-gray-100">
                    <span className="text-sm text-gray-500 font-medium w-44 shrink-0">{t.resStatus}</span>
                    <StatusBadge status={lookupResult.application?.status} t={t} />
                  </div>
                  <InfoRow label={t.resReply} value={lookupResult.application?.universityReply || t.resNoReply} />
                  <InfoRow label={t.resCode}  value={lookupResult.application?.universityCode} />
                  <InfoRow label={t.resDate}
                    value={lookupResult.application?.createdAt
                      ? new Date(lookupResult.application.createdAt).toLocaleDateString(
                          lang === "ar" ? "ar-SA" : "en-GB",
                          { day: "2-digit", month: "short", year: "numeric" })
                      : "—"} />
                </div>

                {lookupResult.application?.universityReply && (
                  <div className="mt-4 p-4 rounded-2xl border leading-relaxed"
                    style={{ background: `${PRIMARY}08`, borderColor: `${PRIMARY}22`, color: "#1e3a8a", fontSize: isRTL ? "0.95rem" : "0.875rem" }}>
                    <p className="font-semibold mb-1">{t.msgFrom}</p>
                    <p>{lookupResult.application.universityReply}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* FOOTER                                                              */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <footer className="relative overflow-hidden"
        style={{ background: "linear-gradient(135deg,#030810 0%,#0a1630 50%,#030810 100%)" }}>
        {/* Blobs */}
        <div className="absolute top-0 left-0 w-80 h-80 rounded-full opacity-10 blur-[110px]"
          style={{ background: PRIMARY }} />
        <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full opacity-10 blur-[110px]"
          style={{ background: ACCENT }} />

        {/* Top gradient line */}
        <div className="h-px w-full"
          style={{ background: `linear-gradient(90deg,transparent,${PRIMARY},${ACCENT},transparent)` }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-8">

          {/* Main footer grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-12">

            {/* Brand col */}
            <div className="flex flex-col gap-4">
              {logoEl}
              <p className="text-sm leading-relaxed" style={{ color: "#94a3b8" }}>
                {t.footerTagline}
              </p>
              <div className="flex items-center gap-3 mt-2">
                {SOCIAL_LINKS.map(({ Icon, href }, i) => (
                  <a key={i} href={href} target="_blank" rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110 hover:brightness-125"
                    style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)" }}>
                    <Icon size={15} className="text-gray-300" />
                  </a>
                ))}
              </div>
            </div>

            {/* Nav col */}
            <div>
              <p className="text-xs font-bold uppercase tracking-widest mb-5" style={{ color: ACCENT }}>
                {isRTL ? "روابط سريعة" : "Quick Links"}
              </p>
              <div className="flex flex-col gap-3">
                {t.navLinks.map((item, i) => (
                  <a key={item} href={t.navAnchors[i]}
                    className="text-sm transition-colors"
                    style={{ color: "#94a3b8" }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "#fff")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "#94a3b8")}>
                    {item}
                  </a>
                ))}
              </div>
            </div>

            {/* Contact col */}
            <div>
              <p className="text-xs font-bold uppercase tracking-widest mb-5" style={{ color: ACCENT }}>
                {t.contactLabel}
              </p>
              <div className="flex flex-col gap-3">
                {t.contactItems.map((item, i) => (
                  <div key={i} className="flex items-center gap-3" style={{ color: "#94a3b8" }}>
                    <ContactIcon type={item.icon} />
                    <span className="text-sm">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom strip */}
          <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-3"
            style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            <p className="text-xs" style={{ color: "#475569" }}>
              © {new Date().getFullYear()}{" "}
              <span style={{ color: ACCENT }}>{companyName}</span>.{" "}
              {t.footerRights}
            </p>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: ACCENT }} />
              <span className="text-xs" style={{ color: "#475569" }}>
                {lang === "en" ? "All systems operational" : "جميع الأنظمة تعمل"}
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
